import React, { type ChangeEvent, type FormEvent, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CategorySelect from './CategorySelect';
import { productsFeatures } from '../config/productsFeatures';

interface ProductFormProps {
  formProduct: {
    nombre: string;
    descripcion: string;
    precio: string | number;
    sku: string;
    stock: string | number;
    categoria: string;
    es_visible: boolean;
    es_comprable: boolean;
    imagen_url: string;
  };
  handleInputChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, productId?: number | null) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isEditing: boolean;
  handleCancelEdit: () => void;
  // New: allow parent to set individual fields programmatically (e.g., after GCS upload)
  setField: (name: string, value: string | number | boolean) => void;
  // New: upload handler provided by parent to perform GCS upload with comercio_key context
  onUploadImage?: (file: File) => Promise<string>;

  // Category selector integration (optional; parent-driven)
  categoriesEnabled?: boolean;
  categoryOptions?: string[];
  onCreateCategory?: (name: string) => void;
  onRenameCategory?: (oldName: string, newName: string) => void;
  // Optional: provide existing SKUs to ensure client-side uniqueness of auto-generated SKU
  existingSkus?: string[];
}

const ProductForm: React.FC<ProductFormProps> = ({
  formProduct,
  handleInputChange,
  handleSubmit,
  isEditing,
  handleCancelEdit,
  setField,
  onUploadImage,
  categoriesEnabled,
  categoryOptions,
  onCreateCategory,
  onRenameCategory,
  existingSkus,
}) => {
  const { t } = useTranslation();
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
 
  // Revoke object URL on unmount or file change to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
    };
  }, [previewUrl]);

  // ===== SKU generation & uniqueness helpers =====
  const getCrypto = (): Crypto | null => {
    // Prefer window.crypto in browsers
    if (typeof window !== 'undefined' && typeof window.crypto?.getRandomValues === 'function') {
      return window.crypto as Crypto;
    }
    // Fallback to globalThis.crypto when available (SSR or non-browser envs)
    const g: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
    if (g && typeof g.crypto?.getRandomValues === 'function') {
      return g.crypto as Crypto;
    }
    return null;
  };

  const randomHex = (bytes: number): string => {
    const c = getCrypto();
    if (c) {
      const arr = new Uint8Array(bytes);
      c.getRandomValues(arr);
      return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    }
    // Fallback (less strong but acceptable with date entropy + UI uniqueness checks)
    let s = '';
    for (let i = 0; i < bytes; i++) {
      s += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    }
    return s.toUpperCase();
  };

  const yyyymmdd = (): string => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}${mm}${dd}`;
  };

  // Modern default SKU format: SKU_YYYYMMDD_8HEX
  const generateDefaultSku = (): string => `SKU_${yyyymmdd()}_${randomHex(4)}`;

  const pickUniqueDefaultSku = (existing?: string[], maxRandomAttempts = 5): string => {
    const set = new Set((existing || []).filter(Boolean).map((s) => s.trim()));
    let sku = generateDefaultSku();
    let tries = 0;
    while (set.has(sku) && tries < maxRandomAttempts) {
      sku = generateDefaultSku();
      tries++;
    }
    if (!set.has(sku)) return sku;
    let i = 1;
    while (set.has(`${sku}-${i}`)) i++;
    return `${sku}-${i}`;
  };

  // Stable example placeholder for UX; created once
  const skuPlaceholderRef = useRef<string>(generateDefaultSku());

  const handleSkuBlur = () => {
    const val = (formProduct.sku ?? '').trim();
    if (!val) {
      setField('sku', pickUniqueDefaultSku(existingSkus));
    }
  };

  const onLocalSubmit = (e: FormEvent<HTMLFormElement>) => {
    const needsSku = !(formProduct.sku ?? '').trim();
    if (needsSku) {
      // Wait one tick for setField to update parent state, then forward submit
      e.preventDefault();
      const generated = pickUniqueDefaultSku(existingSkus);
      setField('sku', generated);
      setTimeout(() => {
        handleSubmit(e);
      }, 0);
      return;
    }
    handleSubmit(e);
  };

  const handleLocalUpload = async (file: File) => {
    if (!onUploadImage) return;
    setUploadError(null);
    try {
      setUploading(true);
      const url = await onUploadImage(file);
      // Set the uploaded URL into the controlled form field
      setField('imagen_url', url);
      setPreviewUrl(url);
    } catch (e: any) {
      setUploadError(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  const triggerBrowse = () => { fileInputRef.current?.click(); };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); if (!dragActive) setDragActive(true); };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0] || null;
    if (f && !f.type.startsWith('image/')) {
      setUploadError((t('productsPage.form.status.uploadError') || 'Upload error') + ': Not an image file');
      return;
    }
    setSelectedImageFile(f);
    setUploadError(null);
    if (f) {
      try {
        const objUrl = URL.createObjectURL(f);
        setPreviewUrl(objUrl);
      } catch {}
      if (onUploadImage) {
        await handleLocalUpload(f);
      }
    }
  };
  return (
    <form onSubmit={onLocalSubmit} className="mx-auto max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-black/20 rounded-lg shadow-xl border border-white/20">
      <div className="md:col-span-2">
        <label htmlFor="categoria" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.category')}:</label>
        {(categoriesEnabled ?? productsFeatures.categories.enabled) ? (
          <CategorySelect
            value={String(formProduct.categoria || '')}
            onChange={(name) => setField('categoria', name)}
            options={categoryOptions || []}
            allowCreate={productsFeatures.categories.allowCreateInline}
            allowRename={productsFeatures.categories.allowRename}
            onCreate={onCreateCategory}
            onRename={onRenameCategory}
            placeholderKey="productsPage.form.placeholders.category"
            className="mt-1"
          />
        ) : (
          <input
            id="categoria"
            type="text"
            name="categoria"
            placeholder={t('productsPage.form.placeholders.category')}
            value={formProduct.categoria}
            onChange={handleInputChange}
            className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        )}
      </div>
      <div>
        <label htmlFor="nombre" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.name')}:</label>
        <input id="nombre" type="text" name="nombre" placeholder={t('productsPage.form.placeholders.name')} value={formProduct.nombre} onChange={handleInputChange} required
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
      </div>
      <div className="md:col-span-2">
        <label htmlFor="descripcion" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.description')}:</label>
        <textarea id="descripcion" name="descripcion" placeholder={t('productsPage.form.placeholders.description')} value={formProduct.descripcion} onChange={handleInputChange} rows={4}
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm resize-y min-h-[96px]"></textarea>
      </div>
      <div>
        <label htmlFor="precio" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.price')}:</label>
        <input id="precio" type="number" name="precio" placeholder={t('productsPage.form.placeholders.price')} value={formProduct.precio} onChange={handleInputChange} step="0.01" min="0" inputMode="decimal" required
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
      </div>
      <div>
        <label htmlFor="sku" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.sku')}:</label>
        <input id="sku" type="text" name="sku" placeholder={skuPlaceholderRef.current} value={formProduct.sku} onChange={handleInputChange} onBlur={handleSkuBlur}
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
      </div>
      <div>
        <label htmlFor="stock" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.stock')}:</label>
        <input id="stock" type="number" name="stock" placeholder={t('productsPage.form.placeholders.stock')} value={formProduct.stock} onChange={handleInputChange} min="0" step="1" inputMode="numeric"
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
      </div>
      {/* category moved to top and set full width */}
      <div className="md:col-span-2">
        <label htmlFor="imagen_url" className="block text-sm font-medium text-white/70 mb-1">{t('productsPage.form.labels.imageUrl')}:</label>
        <input
          id="imagen_url"
          type="text"
          name="imagen_url"
          placeholder={t('productsPage.form.placeholders.imageUrl')}
          value={formProduct.imagen_url}
          onChange={handleInputChange}
          className="mt-1 block w-full px-4 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
        {/* Alternative: Upload to Google Cloud Storage */}
        <div className="mt-3 p-3 border rounded-md bg-black/10">
          <label className="block text-sm font-medium text-white/70 mb-1">
            {t('productsPage.form.labels.imageUrl')} — {t('productsPage.form.labels.optionalUpload') || 'or upload image to GCS'}
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div
              onClick={triggerBrowse}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); triggerBrowse(); } }}
              className={`w-full sm:flex-1 rounded-md border-2 ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-dashed border-white/20 bg-black/20'} px-4 py-6 text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`}
              aria-label={t('productsPage.form.dropzone.dragOrClick', { defaultValue: 'Drag and drop an image, or click to browse' })}
            >
              <svg className="mx-auto h-8 w-8 text-white/40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 7a2 2 0 012-2h2l1-1h4l1 1h2a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm8 3a4 4 0 100 8 4 4 0 000-8z"></path>
              </svg>
              <p className="mt-2 text-xs text-white/70">
                <span className="hidden sm:inline">{t('productsPage.form.dropzone.dragOrClick', { defaultValue: 'Drag and drop an image, or click to browse' })}</span>
                <span className="sm:hidden">{t('productsPage.form.dropzone.tapOrSelect', { defaultValue: 'Tap to select an image' })}</span>
              </p>
              <button
                type="button"
                onClick={triggerBrowse}
                className="mt-2 inline-flex items-center text-xs font-medium text-indigo-700 hover:text-indigo-800 underline underline-offset-2"
              >
                {t('productsPage.form.dropzone.browse', { defaultValue: 'Select file' })}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && !f.type.startsWith('image/')) {
                    setUploadError((t('productsPage.form.status.uploadError') || 'Upload error') + ': Not an image file');
                    return;
                  }
                  setSelectedImageFile(f);
                  setUploadError(null);
                  if (f) {
                    try {
                      const objUrl = URL.createObjectURL(f);
                      setPreviewUrl(objUrl);
                    } catch {}
                  }
                  if (f && onUploadImage) {
                    await handleLocalUpload(f);
                  }
                }}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>
          </div>
          {selectedImageFile && !uploading && (
            <p className="mt-1 text-xs text-white/60 break-all">
              {selectedImageFile.name} ({Math.round(selectedImageFile.size / 1024)} KB)
            </p>
          )}
          {uploading && (
            <p className="mt-1 flex items-center gap-2 text-xs text-blue-600" role="status" aria-live="polite">
              <svg className="h-4 w-4 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              {t('productsPage.form.status.uploading') || 'Uploading image to cloud storage...'}
            </p>
          )}
          {uploadError && (
            <p className="mt-1 text-xs text-red-600" role="status" aria-live="assertive">
              {t('productsPage.form.status.uploadError') || 'Upload error'}: {uploadError}
            </p>
          )}
          {formProduct.imagen_url && (
            <p className="mt-2 text-xs text-green-700 break-all" role="status" aria-live="polite">
              {t('productsPage.form.status.imageReady') || 'Image URL set'}: <a href={formProduct.imagen_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-green-700 hover:text-green-800">{formProduct.imagen_url}</a>
            </p>
          )}
          {previewUrl && (
            <div className="mt-2">
              <p className="text-xs font-medium text-white/70">
                {t('productsPage.form.dropzone.preview', { defaultValue: 'Preview' })}
              </p>
              <img
                src={previewUrl}
                alt="Preview"
                className="mt-1 h-24 w-24 object-contain border rounded"
              />
            </div>
          )}
        </div>
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center">
          <input type="checkbox" name="es_visible" checked={formProduct.es_visible} onChange={handleInputChange}
            className="form-checkbox h-5 w-5 text-indigo-600 rounded focus:ring-indigo-500" />
          <span className="ml-2 text-white/70">{t('productsPage.form.labels.visible')}</span>
        </label>
        <label className="inline-flex items-center">
          <input type="checkbox" name="es_comprable" checked={formProduct.es_comprable} onChange={handleInputChange}
            className="form-checkbox h-5 w-5 text-indigo-600 rounded focus:ring-indigo-500" />
          <span className="ml-2 text-white/70">{t('productsPage.form.labels.purchasable')}</span>
        </label>
      </div>
      <div className="md:col-span-2 flex flex-col md:flex-row gap-3">
        <button type="submit"
          className={`btn-taratic ${isEditing ? 'btn-green' : 'btn-blue'} flex-1 flex justify-center py-2.5 px-4 border rounded-md text-base font-medium focus:outline-none transition duration-300`}>
          {isEditing ? t('productsPage.form.buttons.submitUpdate') : t('productsPage.form.buttons.submitCreate')}
        </button>
        {isEditing && (
          <button type="button" onClick={handleCancelEdit}
            className="btn-taratic btn-ghost-dark flex-1 flex justify-center py-2.5 px-4 border rounded-md text-base font-medium focus:outline-none transition duration-300">
            {t('productsPage.form.buttons.cancelEdit')}
          </button>
        )}
      </div>
    </form>
  );
}

export default ProductForm;
